"use client";

import { useEffect, useState, type MouseEvent, type JSX } from "react";
import { useLenis } from "@/components/providers/SmoothScrollProvider";
import { CONSULTATION_URL, WA_NUMBERS } from "@/lib/consultation-links";
import { openWhatsAppWithUtm } from "@/lib/whatsapp";
import { ctaButtonClassName } from "@/lib/cta-styles";
import { setStickyCtaVisibility } from "@/lib/floating-cta-visibility";
import { scrollToHash } from "@/lib/scroll-to-hash";
import { cn } from "@/lib/utils";

const HERO_ID = "hero-cta-buttons";

interface StickyBottomCtaProps {
  /** Defaults mirror home-v3's hero — the only page with no CMS content to pull from. */
  primaryLabel?: string;
  primaryHref?: string;
  secondaryLabel?: string;
  /** @deprecated Pass whatsappNamespace instead — secondary now uses rotation by default. */
  secondaryHref?: string;
  whatsappNamespace?: string;
}

export const StickyBottomCta = ({
  primaryLabel = "Book a Consultation Call",
  primaryHref = CONSULTATION_URL,
  secondaryLabel = "Talk to a Specialist",
  secondaryHref,
  whatsappNamespace = "sticky",
}: StickyBottomCtaProps = {}): JSX.Element => {
  const lenisRef = useLenis();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const hero = document.getElementById(HERO_ID);

    // Pages with a hero section: show once it's scrolled fully out of view.
    if (hero) {
      const observer = new IntersectionObserver(
        ([entry]) => setVisible(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0),
        { threshold: 0 },
      );
      observer.observe(hero);
      return () => observer.disconnect();
    }

    // Pages without one: fall back to scrolling past the first screenful.
    const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.9);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Published so the chat nudge bubble can sit above this bar only while it is
  // actually on screen.
  useEffect(() => {
    setStickyCtaVisibility(visible);
  }, [visible]);

  // Pages that never render this bar must not inherit a stale "visible".
  useEffect(() => () => setStickyCtaVisibility(false), []);

  return (
    <div
      aria-hidden={!visible}
      className={`chat-safe-floating-cta fixed inset-x-0 z-40 flex justify-center px-4 transition-all duration-300 ${
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
      }`}
    >
      <div className="flex flex-nowrap items-center justify-center gap-2 sm:gap-3 rounded-[40px] border border-white/10 bg-[#0f1218]/95 p-2 shadow-[0px_8px_24px_rgba(0,0,0,0.5)] backdrop-blur-sm">
        {/* Secondary — WhatsApp rotation by default; falls back to explicit href if provided */}
        {secondaryHref ? (
          <a
            href={secondaryHref}
            target="_blank"
            rel="noopener noreferrer"
            tabIndex={visible ? 0 : -1}
            className={cn(ctaButtonClassName("secondary"), "px-3 py-2.5 sm:px-5 sm:py-4")}
          >
            {secondaryLabel}
          </a>
        ) : (
          <button
            type="button"
            tabIndex={visible ? 0 : -1}
            onClick={() => openWhatsAppWithUtm(WA_NUMBERS, whatsappNamespace)}
            className={cn(ctaButtonClassName("secondary"), "cursor-pointer px-3 py-2.5 sm:px-5 sm:py-4")}
          >
            {secondaryLabel}
          </button>
        )}
        {/* Primary */}
        {(() => {
          const isAnchor = primaryHref.startsWith("#");
          return (
            <a
              href={primaryHref}
              {...(isAnchor
                ? {
                    onClick: (event: MouseEvent) => {
                      event.preventDefault();
                      event.stopPropagation();
                      scrollToHash(lenisRef.current, primaryHref);
                    },
                  }
                : { target: "_blank", rel: "noopener noreferrer" })}
              tabIndex={visible ? 0 : -1}
              className={cn(ctaButtonClassName("primary"), "px-3 py-2.5 sm:px-5 sm:py-4")}
            >
              {primaryLabel}
            </a>
          );
        })()}
      </div>
    </div>
  );
};
